import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, RefreshCw, Pencil, Search, FileText } from "lucide-react";
import { adminListArticles } from "@/admin/cmsApi";

export default function AdminArticlesList() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminListArticles();
      setItems(data.items || []);
      setCategories(data.categories || []);
    } catch (e) {
      setError(e.message || "Failed to load articles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const qNorm = q.trim().toLowerCase();
    return items.filter((a) => {
      if (cat && a.category !== cat) return false;
      if (qNorm) {
        const hay = `${a.slug} ${a.title?.en || ""}`.toLowerCase();
        if (!hay.includes(qNorm) && !(a.title?.ar || "").includes(q.trim())) return false;
      }
      return true;
    });
  }, [items, q, cat]);

  const categoryOptions = useMemo(
    () => [{ value: "", label: `All categories (${items.length})` }]
      .concat(categories.map((c) => ({
        value: c.id,
        label: `${c.label?.en || c.id} (${items.filter((a) => a.category === c.id).length})`,
      }))),
    [categories, items]
  );

  return (
    <div data-testid="admin-articles-list">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="sas-eyebrow mb-2">Content</div>
          <h1 className="sas-h1">Articles</h1>
          <p className="sas-body mt-3 max-w-2xl text-[15px]">
            Create, edit, and delete the articles shown on the public
            <code className="mx-1 px-1 py-0.5 rounded bg-[color:var(--sas-accent-soft)] text-[12px]">/articles</code>
            section. Each article supports full Arabic + English copy, SEO meta, related tool slugs, and structured body blocks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-articles-refresh"
          >
            <RefreshCw size={14} aria-hidden /> Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/articles/new")}
            className="sas-btn sas-btn-primary"
            data-testid="admin-articles-new"
          >
            <Plus size={16} aria-hidden /> New article
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3">
        <div className="relative">
          <Search size={14} aria-hidden className="absolute top-1/2 -translate-y-1/2 start-3 text-[color:var(--sas-ink-2)]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles by title or slug…"
            className="sas-input ps-9"
            data-testid="admin-articles-search"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="sas-input"
          data-testid="admin-articles-category-filter"
        >
          {categoryOptions.map((o) => (
            <option key={o.value || "all"} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-6">
        {error ? (
          <div className="rounded-xl border border-[color:#fecaca] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#991b1b]" role="alert" data-testid="admin-articles-error">
            {error}
          </div>
        ) : loading ? (
          <div className="space-y-2" data-testid="admin-articles-loading">
            {[0,1,2,3].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-white border border-[color:var(--sas-line)] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[color:var(--sas-line)] rounded-xl py-16 text-center" data-testid="admin-articles-empty">
            <div className="w-11 h-11 rounded-full mx-auto flex items-center justify-center mb-3" style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }} aria-hidden>
              <FileText size={18} />
            </div>
            <h2 className="sas-h3">No articles match</h2>
            <p className="sas-body mt-2 text-[14px]">Try clearing the filter, or create a new article.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--sas-line)] border border-[color:var(--sas-line)] rounded-xl overflow-hidden bg-white" data-testid="admin-articles-rows">
            {filtered.map((a) => {
              const catLabel = categories.find((c) => c.id === a.category)?.label?.en || a.category;
              return (
                <li key={a.id} data-testid={`admin-article-row-${a.id}`}>
                  <div className="flex items-start gap-4 px-4 py-3.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold tracking-wide uppercase text-[color:var(--sas-ink-2)]">
                        {catLabel} · {a.slug}
                      </div>
                      <div className="mt-1 text-[14.5px] font-semibold text-[color:var(--sas-ink)]">
                        {a.title?.en || a.title?.ar || "(untitled)"}
                      </div>
                      <div className="mt-0.5 text-[12.5px] text-[color:var(--sas-ink-2)] line-clamp-2">
                        {a.excerpt?.en || a.excerpt?.ar || ""}
                      </div>
                    </div>
                    <Link
                      to={`/admin/articles/${a.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--sas-ink)] px-2.5 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)]"
                      data-testid={`admin-article-edit-${a.slug}`}
                    >
                      <Pencil size={12} aria-hidden /> Edit
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
